import React from "react";
import { Box, Dialog, CircularProgress } from "@mui/material";
import { displayFont, tokens } from "../theme";
import InlineMessage from "./InlineMessage";

/**
 * A sheet: the shape a short request takes.
 *
 * The module-screens book allows exactly two shapes for creating anything. A
 * short request — one answerable in about four fields — is a sheet over the
 * screen you were on. Anything with legal or payroll consequence is a stepped
 * page instead. The dividing line is not length, it is whether being half-done
 * matters: a sheet creates nothing until it is sent, so it needs no draft state
 * and losing it costs nothing.
 *
 * Three rules are built into this component rather than left to each caller:
 *
 *   The consequence is shown before the button. The book lists what that means
 *   for a leave request: days used, balance left, who else is away, who will
 *   decide. So `consequence` sits between the fields and the actions, and
 *   `routesTo` names the person it goes to — a request whose destination is
 *   unknown until after sending is one people re-send.
 *
 *   The primary action names what happens next. "Send request", not "Submit".
 *   The label is required for that reason; there is no default.
 *
 *   The screen behind stays visible. The scrim is the book's plum at 32%, and
 *   cancelling unmounts nothing underneath, so the caller returns to the same
 *   scroll position with nothing lost.
 */
const Sheet = ({
  open,
  onClose,
  title,
  children,
  consequence,
  warning,
  routesTo,
  submitLabel,
  onSubmit,
  submitting,
  cancelLabel = "Cancel",
  disabled,
}) => (
  <Dialog
    open={Boolean(open)}
    onClose={onClose}
    // The book's sheet is 460px. Wider than that and the fields stop reading as
    // a single question and start reading as a form.
    PaperProps={{
      component: onSubmit ? "form" : "div",
      onSubmit: onSubmit
        ? (e) => { e.preventDefault(); onSubmit(e); }
        : undefined,
      sx: {
        width: "28.75rem",
        maxWidth: "100%",
        borderRadius: "12px",
        border: `1px solid ${tokens.border}`,
        boxShadow: "0 16px 40px -20px rgba(42,29,34,0.22)",
        padding: 0,
        margin: "1rem",
      },
    }}
  >
    <Box sx={{ padding: "20px 22px", borderBottom: `1px solid ${tokens.border}` }}>
      <Box sx={{ fontFamily: displayFont, fontSize: "1.1875rem", fontWeight: 700, color: tokens.text }}>
        {title}
      </Box>
    </Box>

    <Box sx={{ padding: "22px", display: "flex", flexDirection: "column", gap: "20px" }}>
      {children}

      {consequence && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            padding: "14px 16px",
            background: tokens.surface,
            borderRadius: "8px",
            fontSize: "0.875rem",
          }}
        >
          {consequence}
        </Box>
      )}

      {/* Something the requester should know before pressing send, not after —
          a clash with someone else's leave, a month-end close. */}
      {warning && <InlineMessage tone="warning">{warning}</InlineMessage>}

      {routesTo && (
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{routesTo}</Box>
      )}
    </Box>

    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "12px",
        padding: "0 22px 22px",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onClose}
        sx={{
          background: "transparent",
          border: `1px solid ${tokens.borderStrong}`,
          color: tokens.primary,
          borderRadius: "8px",
          padding: "11px 20px",
          fontSize: "0.875rem",
          fontWeight: 600,
          fontFamily: "inherit",
          cursor: "pointer",
          "&:hover": { background: tokens.surfaceSubtle },
        }}
      >
        {cancelLabel}
      </Box>
      <Box
        component="button"
        type={onSubmit ? "submit" : "button"}
        disabled={submitting || disabled}
        sx={{
          background: tokens.primary,
          color: tokens.card,
          border: "none",
          borderRadius: "8px",
          padding: "11px 20px",
          fontSize: "0.875rem",
          fontWeight: 600,
          fontFamily: "inherit",
          cursor: submitting || disabled ? "default" : "pointer",
          "&:hover": { background: tokens.primaryHover },
          "&:disabled": { background: tokens.border, color: tokens.textDisabled },
        }}
      >
        {submitting ? <CircularProgress size={"1.125rem"} sx={{ color: "inherit" }} /> : submitLabel}
      </Box>
    </Box>
  </Dialog>
);

/** One line of the consequence block: what it is, and the figure. */
export const ConsequenceRow = ({ label, value, strong }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
    <Box sx={{ color: tokens.textMuted }}>{label}</Box>
    <Box sx={{ fontWeight: strong ? 700 : 600, color: tokens.text, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </Box>
  </Box>
);

export default Sheet;
